import { Injectable, inject } from '@angular/core';
import { Observable, catchError, forkJoin, map, of, shareReplay, switchMap } from 'rxjs';

import { SAVING_ARENA_API_CONFIG } from '../api/api.config';
import { FrontendEvent } from '../api/api.models';
import { SavingArenaApiService } from '../api/saving-arena-api.service';

/** The current featured event, resolved to the links the header and BIG SALE banner render. */
export interface FeaturedEventLinks {
  name: string;
  /** The event's coupon page (`/event/<slug>`), '' when the event has no slug. */
  couponPath: string;
  /**
   * Event named by the deals link. Usually the featured event, but see `dealsPath` — it can be a
   * different event, so the label must follow this rather than `name`.
   */
  dealsName: string;
  /**
   * Ready-made link labels, e.g. "Labor Day Sale 2026 Coupons".
   *
   * Built here rather than as `{{ name }} Coupons` in each template because event names often
   * already end in the word — "Back to School Deals 2026" rendered as "Back to School Deals 2026
   * Deals". These drop the redundant suffix.
   */
  couponLabel: string;
  dealsLabel: string;
  /**
   * A DEALS page (`/deals/event/<slug>`), or '' when no event has one.
   *
   * An event only HAS a deals page once the stores assigned to it carry enough active products. So
   * this prefers the featured event and otherwise falls back to the richest event that does qualify
   * — the promo slot stays useful instead of disappearing whenever the featured event happens to
   * have no products yet. Either way it is a page that exists: callers still hide the link when this
   * is empty, rather than pointing at a 404 the way the old hardcoded `href="#"` did.
   */
  dealsPath: string;
}

/** One store logo in the "{Event} Top Stores" row. */
export interface FeaturedEventStore {
  id: string;
  name: string;
  logoUrl: string;
  path: string;
}

/** The "{Event} Top Stores" row — the heading plus the logos under it. */
export interface FeaturedEventStores {
  heading: string;
  stores: FeaturedEventStore[];
}

/**
 * Resolves the featured event once and shares it.
 *
 * Before this, `pickFeaturedEvent` was copy-pasted into the header and the BIG SALE banner, and the
 * banner's markup existed in three more places — each with its own hardcoded "Black Friday Deals"
 * link. One source of truth here means adding the deals link fixed every one of them at the same
 * time, and the event lookup runs once per page instead of per component.
 */
@Injectable({ providedIn: 'root' })
export class FeaturedEventService {
  private readonly api = inject(SavingArenaApiService);
  private readonly apiConfig = inject(SAVING_ARENA_API_CONFIG);
  private request$?: Observable<FeaturedEventLinks | null>;
  private stores$?: Observable<FeaturedEventStores | null>;
  private rawEvent$?: Observable<FrontendEvent | null>;

  /** Cached for the app's lifetime — the featured event does not change within a page view. */
  featured(): Observable<FeaturedEventLinks | null> {
    if (!this.request$) {
      this.request$ = forkJoin({
        events: this.api.getEvents({ page: 1, limit: 100 }).pipe(
          map((response) => response.data ?? []),
          catchError(() => of([] as FrontendEvent[])),
        ),
        // Only qualifying events are returned here, ordered richest-first, so this list is both the
        // "has a deals page" test and the fallback ranking.
        dealsEvents: this.api.getDealsEvents(50).pipe(
          map((response) => (response.data ?? []).filter((item) => item.slug)),
          catchError(() => of([] as { name: string; slug: string }[])),
        ),
      }).pipe(
        map(({ events, dealsEvents }) => {
          const event = this.pickFeaturedEvent(events);
          if (!event) return null;

          const slug = String(event.slug || '').trim();
          const featuredHasDeals = slug && dealsEvents.some((item) => item.slug === slug);
          const dealsEvent = featuredHasDeals
            ? { name: event.name?.trim() ?? '', slug }
            : dealsEvents[0];

          const name = event.name?.trim() ?? '';
          const dealsName = dealsEvent?.name?.trim() ?? '';

          return {
            name,
            couponPath: slug ? `/event/${slug}` : '',
            dealsName,
            couponLabel: this.linkLabel(name, 'Coupons'),
            dealsPath: dealsEvent?.slug ? `/deals/event/${dealsEvent.slug}` : '',
            dealsLabel: this.linkLabel(dealsName, 'Deals'),
          };
        }),
        catchError(() => of(null)),
        shareReplay({ bufferSize: 1, refCount: false }),
      );
    }

    return this.request$;
  }

  /**
   * The featured event's top-store logos — the "{Event} Top Stores" row the home page leads with.
   *
   * Resolved from the SAME featured event as `featured()`, so whichever event the home page is
   * promoting is the one every other page showing this row is promoting too: change the event in
   * admin and both move together.
   *
   * Cached for the app's lifetime like `featured()`.
   */
  featuredStores(): Observable<FeaturedEventStores | null> {
    if (!this.stores$) {
      this.stores$ = this.rawFeatured().pipe(
        switchMap((event) => {
          const key = String(event?.slug || event?.id || '').trim();
          if (!event || !key) return of(null);

          return this.api.getEventTopStores(key).pipe(
            map((response) => this.toFeaturedStores(event.name?.trim() ?? '', response?.data)),
            catchError(() => of(null)),
          );
        }),
        catchError(() => of(null)),
        shareReplay({ bufferSize: 1, refCount: false }),
      );
    }

    return this.stores$;
  }

  /** The featured event document itself, cached — both public methods build on this one lookup. */
  private rawFeatured(): Observable<FrontendEvent | null> {
    if (!this.rawEvent$) {
      this.rawEvent$ = this.api.getEvents({ page: 1, limit: 100 }).pipe(
        map((response) => this.pickFeaturedEvent(response.data ?? [])),
        catchError(() => of(null)),
        shareReplay({ bufferSize: 1, refCount: false }),
      );
    }

    return this.rawEvent$;
  }

  /**
   * The event detail payload nests its stores under `data.topStoresFeatured` (the circular-logo
   * row). Falls back to `data.topStores` so a partly-configured event still fills the row, and
   * drops entries with no logo — this row is nothing but logos.
   */
  private toFeaturedStores(eventName: string, event: FrontendEvent | null): FeaturedEventStores | null {
    const data = event?.data;
    if (!data || typeof data !== 'object' || Array.isArray(data)) return null;

    const record = data as Record<string, unknown>;
    const featured = Array.isArray(record['topStoresFeatured']) ? record['topStoresFeatured'] : [];
    const source =
      featured.length > 0 ? featured : Array.isArray(record['topStores']) ? record['topStores'] : [];

    const stores = (source as unknown[])
      .filter((item): item is Record<string, unknown> => !!item && typeof item === 'object')
      .map((store) => {
        const name = typeof store['name'] === 'string' ? store['name'] : '';
        const slug = typeof store['slug'] === 'string' ? store['slug'].trim() : '';
        const logoPath =
          typeof store['logoImagePath'] === 'string' ? store['logoImagePath'].trim() : '';

        return {
          id: typeof store['id'] === 'string' ? store['id'] : slug || name,
          name,
          logoUrl: logoPath ? this.toAssetUrl(logoPath) : '',
          path: slug ? `/${slug}` : '',
        };
      })
      .filter((store) => store.logoUrl.length > 0 && store.path.length > 0);

    if (stores.length === 0) return null;

    return {
      heading: eventName ? `${eventName} Top Stores` : 'Top Stores',
      stores,
    };
  }

  private toAssetUrl(path: string): string {
    if (/^https?:\/\//i.test(path)) return path;

    const base = this.apiConfig.assetBaseUrl.replace(/\/$/, '');

    return `${base}${path.startsWith('/') ? path : `/${path}`}`;
  }

  /**
   * "<Event> Coupons" / "<Event> Deals", without repeating a word the name already ends with —
   * "Back to School Deals 2026" + "Deals" would otherwise read "…Deals 2026 Deals". A trailing year
   * is ignored when checking, since that is where event names usually put it.
   */
  private linkLabel(name: string, suffix: 'Coupons' | 'Deals'): string {
    if (!name) return suffix;

    const singular = suffix === 'Coupons' ? 'coupon' : 'deal';
    // Drop a trailing year ("… 2026") so "Back to School Deals 2026" still counts as ending in "Deals".
    const withoutYear = name.replace(/\s+\d{4}$/, '').trim();
    const lastWord = withoutYear.split(/\s+/).pop()?.toLowerCase() ?? '';

    return lastWord === singular || lastWord === `${singular}s` ? name : `${name} ${suffix}`;
  }

  /**
   * The event to promote, in order of preference:
   *
   *   1. Running now, the one ending soonest, because it is the most urgent.
   *   2. Not started yet, the one starting soonest. A dated event that has not begun is still worth
   *      promoting; people search for Black Friday weeks before it arrives.
   *   3. Already finished, the one that ended most recently.
   *
   * The third exists so the site is never left with no event at all. Without it, the moment the
   * last dated event passed, the header link and the event row would simply vanish, and the fix
   * would be to notice and add more events. A recently ended event still leads somewhere real, and
   * it reappears the instant a future one is added.
   */
  private pickFeaturedEvent(events: FrontendEvent[]): FrontendEvent | null {
    const now = Date.now();
    const dated = events
      .map((event) => ({
        event,
        start: this.parseEventDateMs(event.startDate),
        end: this.parseEventDateMs(event.endDate),
      }))
      .filter((item): item is { event: FrontendEvent; start: number; end: number | null } =>
        item.start !== null,
      );

    const active = dated
      .filter((item) => item.start <= now && (item.end === null || item.end >= now))
      .sort((a, b) => (a.end ?? Number.MAX_SAFE_INTEGER) - (b.end ?? Number.MAX_SAFE_INTEGER));

    if (active.length > 0) return active[0].event;

    const upcoming = dated.filter((item) => item.start > now).sort((a, b) => a.start - b.start);
    if (upcoming.length > 0) return upcoming[0].event;

    // Everything has ended. Show the most recent rather than nothing.
    const past = dated
      .filter((item) => item.end !== null && item.end < now)
      .sort((a, b) => (b.end ?? 0) - (a.end ?? 0));

    return past.length > 0 ? past[0].event : null;
  }

  private parseEventDateMs(value?: string): number | null {
    if (!value) return null;

    const ms = new Date(value).getTime();
    return Number.isNaN(ms) ? null : ms;
  }
}
